import { useId } from 'react';
import { cn } from './format';

export type BrandProduct =
  | 'nxivo'
  | 'shiftbeat'
  | 'identitybeat'
  | 'infrabeat'
  | 'bindley'
  | 'delhiwholesale'
  | 'neerajonline'
  | 'nxstarter'
  | 'nxdxk'
  | 'nxnote'
  | 'releasebeat'
  | 'trackmyexpense'
  | 'scriptorium'
  | 'tempo'
  | 'paybeat'
  | 'nxcare'
  | 'nxhealth'
  | 'nxnurture'
  | 'nxlead'
  | 'nxerp'
  | 'nxivo-admin';

export type BrandMarkProps = {
  size?: number;
  className?: string;
  title?: string;
  /** Product mark variant. Default: nxivo platform mark. */
  product?: BrandProduct;
};

const titles: Record<BrandProduct, string> = {
  nxivo: 'Nxivo',
  shiftbeat: 'ShiftBeat',
  identitybeat: 'IdentityBeat',
  infrabeat: 'InfraBeat',
  bindley: 'Bindley',
  delhiwholesale: 'DelhiWholesale',
  neerajonline: 'Neeraj Online',
  nxstarter: 'NxStarter',
  nxdxk: 'NxDxk',
  nxnote: 'NxNote',
  releasebeat: 'ReleaseBeat',
  trackmyexpense: 'TrackMyExpense',
  scriptorium: 'Scriptorium',
  tempo: 'Tempo',
  paybeat: 'PayBeat',
  nxcare: 'NxCare',
  nxhealth: 'NxHealth',
  nxnurture: 'NxNurture',
  nxlead: 'NxLead',
  nxerp: 'NxERP',
  'nxivo-admin': 'Nxivo Admin',
};

const letterGlyphs: Partial<Record<BrandProduct, string>> = {
  bindley: 'B',
  nxdxk: 'D',
  nxnote: 'N',
  releasebeat: 'R',
  scriptorium: 'S',
  tempo: 'T',
  paybeat: 'P',
  nxcare: 'C',
  nxhealth: 'H',
  nxnurture: 'U',
  nxlead: 'L',
  nxerp: 'E',
  'nxivo-admin': 'A',
  trackmyexpense: 'X',
  delhiwholesale: 'W',
};

/** Nxivo / product brand mark — product-colored squircle via --brand. */
export function BrandMark({
  size = 40,
  className,
  title,
  product = 'nxivo',
}: BrandMarkProps) {
  const gid = useId().replace(/:/g, '');
  const label = title ?? titles[product];
  const showHeartbeat = product === 'shiftbeat';
  const glyph = letterGlyphs[product];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('shrink-0', className)}
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      <rect width="32" height="32" rx="8" fill="var(--brand, #0f5c52)" />
      {showHeartbeat ? (
        <>
          <path
            d="M4 16h5.2l1.8-3.6 2.2 7.2L16.4 8l3.2 12.4L21.6 16H28"
            stroke={`url(#${gid})`}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <defs>
            <linearGradient
              id={gid}
              x1="4"
              y1="16"
              x2="28"
              y2="16"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="48%" stopColor="#ffffff" />
              <stop offset="52%" stopColor="var(--beat, #f5a524)" />
              <stop offset="100%" stopColor="var(--beat, #f5a524)" />
            </linearGradient>
          </defs>
        </>
      ) : glyph ? (
        <text
          x="16"
          y="21.5"
          textAnchor="middle"
          fill="#ffffff"
          fontSize="15"
          fontWeight="700"
          fontFamily="DM Sans, Segoe UI, sans-serif"
        >
          {glyph}
        </text>
      ) : (
        <path
          d="M10 22V10h2.4l5.2 7.6V10H20v12h-2.4L12.4 14.4V22H10z"
          fill="#ffffff"
        />
      )}
    </svg>
  );
}
